/**
 * 菜单实时预览侧边栏 — 模拟 TenantLayout 侧边栏效果
 * 只显示 status=1 且 menu_type != 按钮 的菜单项
 */
import React, { useState } from 'react';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { getIcon } from '@/utils/iconRegistry';
import type { MenuItem as MenuItemType } from '../../api/types';
import { MenuType } from '../../api/types';

interface MenuPreviewProps {
  menuList: MenuItemType[];
  workMode: string;
}

/** 将菜单数据转为 Ant Design Menu items（过滤掉禁用和按钮） */
const convertToMenuItems = (items: MenuItemType[]): MenuProps['items'] => {
  return items
    .filter((item) => item.status === 1 && item.menu_type !== MenuType.BUTTON)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((item) => {
      const IconComp = getIcon(item.icon);
      const icon = IconComp ? <IconComp /> : undefined;
      const children = item.children ? convertToMenuItems(item.children) : undefined;
      const hasChildren = children && children.length > 0;

      if (hasChildren) {
        return {
          key: item.path || String(item.id),
          icon,
          label: item.title,
          children,
        };
      }

      return {
        key: item.path || String(item.id),
        icon,
        label: item.title,
      };
    });
};

const MenuPreview: React.FC<MenuPreviewProps> = ({ menuList, workMode }) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');

  const menuItems = convertToMenuItems(menuList);
  const modeLabel = workMode === 'business' ? '酷采商城' : '管理后台';

  return (
    <div
      style={{
        width: 220,
        background: '#001529',
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
        maxHeight: 'calc(100vh - 180px)',
        position: 'sticky',
        top: 80,
      }}
    >
      {/* Logo 区域 */}
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          borderBottom: '1px solid rgba(255,255,255,.06)',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            background: '#1677ff',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
            marginRight: 10,
            flexShrink: 0,
          }}
        >
          酷
        </div>
        <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{modeLabel}</span>
      </div>

      {/* 菜单区域 */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {menuItems && menuItems.length > 0 ? (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKey ? [selectedKey] : []}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            onClick={({ key }) => setSelectedKey(key)}
            items={menuItems}
            style={{ borderRight: 'none' }}
          />
        ) : (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: 'rgba(255,255,255,.35)',
              fontSize: 13,
            }}
          >
            暂无可见菜单
          </div>
        )}
      </div>

      {/* 底部标签 */}
      <div
        style={{
          padding: '8px 16px',
          borderTop: '1px solid rgba(255,255,255,.06)',
          fontSize: 11,
          color: 'rgba(255,255,255,.25)',
          textAlign: 'center',
        }}
      >
        实时预览 · {workMode}
      </div>
    </div>
  );
};

export default MenuPreview;
